// Runs the temporal analysis (F-03.1 automatic time alignment, F-03.3 drop/insert/duplicate markers) for each B.
// Results are suggestions only (Q-04): the user applies an offset with one click.

import type { App } from '../app/app';
import type { SourceId } from '../core/types';
import { Feeder } from '../media/feeder';
import { detectDiscontinuities, estimateOffset, normalizeSignature, type Discontinuity, type OffsetResult } from '../core/temporal';

export interface TemporalResult {
  source: SourceId;
  offset: OffsetResult | null;
  events: Discontinuity[];
  /** Offset in effect when the analysis ran (events are reported against it). */
  baseOffset: number;
}

export class TemporalManager {
  results = new Map<SourceId, TemporalResult>();
  running: SourceId | null = null;
  progress = 0;
  error: string | null = null;
  private worker: Worker | null = null;
  private seq = 0;
  onChange: (() => void) | null = null;
  private cancelled = false;

  constructor(private app: App) {}

  cancel(): void {
    this.cancelled = true;
  }

  private sigOf(frame: VideoFrame): Promise<Float32Array> {
    this.worker ??= new Worker(new URL('../workers/signature-worker.ts', import.meta.url), { type: 'module' });
    const w = this.worker;
    const id = ++this.seq;
    return new Promise((resolve, reject) => {
      const on = (ev: MessageEvent<{ id: number; data?: Float32Array; error?: string }>) => {
        if (ev.data.id !== id) return;
        w.removeEventListener('message', on);
        if (ev.data.data) resolve(ev.data.data);
        else reject(new Error(ev.data.error));
      };
      w.addEventListener('message', on);
      w.postMessage({ id, frame }, [frame]);
    });
  }

  /** Signatures of every frame of a source (sequential decode on a dedicated worker). */
  private async signatures(id: SourceId, count: number, onStep: (i: number) => void): Promise<(Float32Array | null)[]> {
    const rt = this.app.sources.runtime.get(id)!;
    const feeder = new Feeder(rt.client.media);
    await feeder.open();
    const out: (Float32Array | null)[] = new Array(count).fill(null);
    try {
      for (let i = 0; i < count; i++) {
        if (this.cancelled) break;
        const ahead = [i + 1, i + 2, i + 3, i + 4, i + 5, i + 6].filter((x) => x < count);
        await feeder.get(i, ahead);
        const vf = feeder.take(i);
        if (vf) out[i] = await this.sigOf(vf);
        onStep(i);
      }
    } finally {
      feeder.dispose();
    }
    return out;
  }

  async analyse(id: SourceId): Promise<TemporalResult | null> {
    const app = this.app;
    const a = app.sources.source('A');
    const b = app.sources.source(id);
    if (!a || !b || this.running || id === 'A') return null;
    this.running = id;
    this.cancelled = false;
    this.error = null;
    this.progress = 0;
    this.onChange?.();
    try {
      const na = a.spec.frameCount;
      const nb = b.spec.frameCount;
      const total = na + nb;
      let done = 0;
      const step = () => {
        done++;
        if (done % 8 === 0) {
          this.progress = done / total;
          this.onChange?.();
        }
      };
      const sa = (await this.signatures('A', na, step)).map((s) => (s ? normalizeSignature(s) : null));
      const sb = (await this.signatures(id, nb, step)).map((s) => (s ? normalizeSignature(s) : null));
      if (this.cancelled) return null;
      const maxOff = Math.min(120, Math.floor(Math.max(na, nb) / 3));
      const offset = estimateOffset(sa, sb, maxOff);
      // Events against the currently set offset: B frame j shows A frame j + showOffset, showOffset = −frameOffset.
      const baseOffset = b.frameOffset;
      const show = offset ? -offset.offset : -baseOffset;
      const { events } = detectDiscontinuities(sa, sb, show, 6);
      const r: TemporalResult = { source: id, offset, events, baseOffset };
      this.results.set(id, r);
      return r;
    } catch (e) {
      this.error = String(e);
      return null;
    } finally {
      this.running = null;
      this.progress = 1;
      this.onChange?.();
    }
  }
}
